import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { Modal, SecondaryButton } from '../../components';
import { BsInfoCircle, BsEnvelope, BsBank } from 'react-icons/bs';
import { FREEDOMLY } from '../../util/receipts/freedomly';

import css from './TransactionPanel.css';

const { Money } = sdkTypes;

const InvoiceInfoModal = ({
  isOpen,
  onClose,
  onManageDisableScrolling,
  customOfferData,
  intl,
  customerName,
  transactionId,
  providerNet,
}) => {
  if (!customOfferData) return null;

  // Net earning after Freedomly's fee (VAT 0 %): the caller's previewTotals
  // providerNet (TransactionPanel's panelTotals / ProcessStepsCard's vatTotals),
  // the same figure the offer card prints as "You Will Earn". payoutTotal is
  // unreliable here (null in the pro process, gross-with-VAT in light), so it
  // is not used.
  const invoiceAmount = new Money(providerNet || 0, 'EUR');

  return (
    <Modal
      id="InvoiceInfoModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.invoiceInfoModalContent}>
        <div className={css.invoiceInfoModalHeader}>
          <h2 className={css.invoiceInfoModalTitle}>
            <FormattedMessage id="InvoiceInfoModal.title" />
          </h2>
        </div>

        {/* Amount to Invoice Section */}
        <div className={css.invoiceInfoSection}>
          <div className={css.invoiceAmountBox}>
            <div className={css.invoiceAmountRow}>
              <span className={css.invoiceAmountLabel}>
                <FormattedMessage id="InvoiceInfoModal.amountToInvoice" />
              </span>
              <span className={css.invoiceAmountValue}>{formatMoney(intl, invoiceAmount)}</span>
            </div>
            <p className={css.invoiceAmountNote}>
              <FormattedMessage
                id="InvoiceInfoModal.amountNote"
                values={{
                  customerName: customerName || <FormattedMessage id="InvoiceInfoModal.customer" />,
                }}
              />
            </p>
          </div>
        </div>

        {/* E-Invoice Section */}
        <div className={css.invoiceInfoSection}>
          <h3 className={css.invoiceInfoSectionTitle}>
            <BsBank className={css.invoiceMethodIcon} />
            <FormattedMessage id="InvoiceInfoModal.eInvoiceTitle" />
          </h3>
          <p className={css.invoiceMethodDescription}>
            <FormattedMessage id="InvoiceInfoModal.eInvoicePreferred" />
          </p>
          <div className={css.invoiceDetailsBox}>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.companyName" />
              </span>
              <span className={css.invoiceDetailValue}>{FREEDOMLY.name}</span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.contactPerson" />
              </span>
              <span className={css.invoiceDetailValue}>{FREEDOMLY.invoiceContactPerson}</span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.businessId" />
              </span>
              <span className={css.invoiceDetailValue}>{FREEDOMLY.businessId}</span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.address" />
              </span>
              <span
                className={css.invoiceDetailValue}
              >{`${FREEDOMLY.street}, ${FREEDOMLY.postalCode} ${FREEDOMLY.city}`}</span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.eInvoiceAddress" />
              </span>
              <span className={css.invoiceDetailValue}>{FREEDOMLY.eInvoiceAddress}</span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.operatorId" />
              </span>
              <span className={css.invoiceDetailValue}>{FREEDOMLY.eInvoiceOperatorId}</span>
            </div>
          </div>
        </div>

        {/* Email Invoice Section */}
        <div className={css.invoiceInfoSection}>
          <h3 className={css.invoiceInfoSectionTitle}>
            <BsEnvelope className={css.invoiceMethodIcon} />
            <FormattedMessage id="InvoiceInfoModal.emailInvoiceTitle" />
          </h3>
          <p className={css.invoiceMethodDescription}>
            <FormattedMessage id="InvoiceInfoModal.emailInvoiceDescription" />
          </p>
          <div className={css.invoiceEmailBox}>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.emailAddress" />
              </span>
              <span className={css.invoiceDetailValue}>
                <a href={`mailto:${FREEDOMLY.email}`} className={css.invoiceEmailLink}>
                  {FREEDOMLY.email}
                </a>
              </span>
            </div>
            <div className={css.invoiceDetailRow}>
              <span className={css.invoiceDetailLabel}>
                <FormattedMessage id="InvoiceInfoModal.emailSubject" />
              </span>
              <span className={css.invoiceDetailValue}>
                <FormattedMessage
                  id="InvoiceInfoModal.emailSubjectExample"
                  values={{
                    transactionId: transactionId || '',
                  }}
                />
              </span>
            </div>
          </div>
        </div>
        <div className={css.invoiceInfoFooter}>
          <a
            href="https://help.freedomly.io/fi/articles/6727816-freedomly-kevytyrittajalle"
            target="_blank"
            rel="noopener noreferrer"
            className={css.invoiceHelpLink}
          >
            <FormattedMessage id="InvoiceInfoModal.moreInfo" />
          </a>
        </div>

        <div className={css.invoiceInfoModalButtons}>
          <SecondaryButton onClick={onClose}>
            <FormattedMessage id="InvoiceInfoModal.close" />
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceInfoModal;
